import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Search, Loader2, X, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const MUNI = ['city', 'town', 'township', 'village', 'borough', 'municipality'];

export const JurisdictionPicker = ({ state, initialQuery, onPick, onClose }) => {
  const [q, setQ] = useState(initialQuery || '');
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const res = await axios.get(`${API}/api/jurisdictions`, { params: { state, search: q || undefined, active_only: true, page_size: 15, search_field: 'name' } });
        setRows(res.data?.jurisdictions || []);
      } catch { setRows([]); } finally { setBusy(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, state]);

  return (
    <div className="mt-2 rounded-lg border border-blue-200 bg-white p-2.5 shadow-sm" data-testid="mls-jurisdiction-picker">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${state} municipalities or counties…`}
            className="w-full h-8 pl-7 pr-2 text-xs rounded-md border border-slate-200 focus:outline-none focus:border-blue-400"
            data-testid="mls-jurisdiction-picker-search" />
        </div>
        <button type="button" onClick={onClose} className="h-7 w-7 grid place-items-center rounded text-slate-400 hover:text-slate-600" data-testid="mls-jurisdiction-picker-close"><X className="w-3.5 h-3.5" /></button>
      </div>
      <div className="mt-2 max-h-48 overflow-y-auto divide-y divide-slate-100">
        {busy && <div className="py-3 text-center text-xs text-slate-400"><Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" />Searching…</div>}
        {!busy && rows.length === 0 && <div className="py-3 text-center text-xs text-slate-400">No matching jurisdictions in the directory</div>}
        {!busy && rows.map((j) => {
          const t = (j.jurisdiction_type || '').toLowerCase();
          const isMuni = MUNI.includes(t);
          const isCounty = ['county', 'parish'].includes(t);
          if (!isMuni && !isCounty) return null;
          return (
            <button key={j.id} type="button" onClick={() => onPick(j, isMuni)}
              className="w-full flex items-center justify-between px-2 py-1.5 text-left text-xs hover:bg-blue-50 transition-colors"
              data-testid={`mls-jurisdiction-option-${j.id}`}>
              <span className="font-medium text-slate-800">{j.jurisdiction_name}</span>
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${isCounty ? 'bg-slate-100 text-slate-600' : 'bg-blue-50 text-blue-700'}`}>{isCounty ? 'County' : t.charAt(0).toUpperCase() + t.slice(1)}{j.parent_county_name ? ` · ${j.parent_county_name}` : ''}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* MLS-JUR-UI-001: "Change Jurisdiction" lives beside "Change Entity" in ENTITY & CONTACT.
   Same order-level PUT /mls-jurisdiction as before — only the control moved. */
export const ChangeJurisdictionButton = ({ resolution, open, onToggle, disabled, className = '' }) => {
  if (!resolution) return null;
  const unresolved = !resolution.municipality;
  return (
    <button type="button" onClick={onToggle} disabled={disabled}
      className={`${className} border ${open ? 'border-blue-400 bg-blue-50 text-blue-800' : unresolved ? 'border-amber-300 bg-amber-50 text-amber-800 hover:border-amber-400' : 'border-slate-300 bg-white text-slate-700 hover:border-blue-300 hover:text-blue-700'}`}
      data-testid="mls-jurisdiction-change-btn" data-resolution={resolution.resolution} data-municipality={resolution.municipality?.id || ''}>
      {unresolved ? <AlertTriangle className="w-3.5 h-3.5" /> : <MapPin className="w-3.5 h-3.5" />}{unresolved ? 'Select Jurisdiction' : 'Change Jurisdiction'}
    </button>
  );
};

export const JurisdictionPickerPanel = ({ resolution, onSet, onClose }) => {
  const { municipality, property } = resolution || {};
  const pick = async (j, isMuni) => {
    try {
      await onSet(isMuni ? { municipality_id: j.id } : { county_id: j.id });
      toast.success(`Jurisdiction set to ${j.jurisdiction_name}`);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to set jurisdiction'); }
  };
  return <JurisdictionPicker state={property?.state} initialQuery={municipality ? '' : property?.city} onPick={pick} onClose={onClose} />;
};
